/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: EditProjectDialog.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-PROJECTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * EditProjectDialog - Диалог редактирования проекта с превью карточки и оверхедом.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - UX: вертикальный скролл контента при нехватке высоты экрана.
 * LAST_CHANGE: 2026-07-14 - UX: заголовок портфеля, live-превью карточки, секция оверхеда, primary CTA.
 * LAST_CHANGE: 2026-07-13 - Общие хуки blur-валидации.
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций, парные END_METHOD, блок отправки.
 * END_CHANGE_SUMMARY
 */

import { useState, useEffect, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import ProjectAvatar from '../../../components/ProjectAvatar';
import { getColorByName } from '../../../utils/projectColors';
import { APP_FORM_AUTOCOMPLETE, mergeAppDataFieldSlotProps } from '../../../utils/formFieldConfig';
import { useDialogFormReady } from '../../../hooks/useDialogFormReady';
import { useBlurValidatedField } from '../../../hooks/useBlurValidatedField';
import { isNonEmptyTrimmed } from '../../../utils/formValidators';
import ProjectOverheadSection from './ProjectOverheadSection';

const displayFont = '"Syne", "Inter", sans-serif';

function formatProjectDate(date) {
  if (!date) return '';
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/* START_METHOD_EditProjectDialog */
/* START_CONTRACT: EditProjectDialog
 * PURPOSE: Диалог редактирования проекта (название) и секция накладных расходов.
 * INPUTS: { open: boolean, onClose: ()=>void, onUpdate: (id,payload)=>Promise, project: Project }.
 * OUTPUTS: { JSX.Element|null - диалог }.
 * SIDE_EFFECTS: вызов onUpdate (HTTP наверх); локальный state.
 * LINKS: M-FE-PROJECTS
 * END_CONTRACT: EditProjectDialog
 */
export default function EditProjectDialog({ open, onClose, onUpdate, project }) {
  const firstFieldRef = useRef(null);
  const { formReady, resetFormReady, dialogTransitionSlotProps } = useDialogFormReady({ firstFieldRef });
  const nameField = useBlurValidatedField('', isNonEmptyTrimmed, formReady);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (project) {
      nameField.reset(project.name);
    }
  }, [project]);

  if (!project) return null;

  const trimmedName = nameField.value.trim();
  const previewName = trimmedName || project.name || 'Проект';
  const accentColor = trimmedName ? getColorByName(trimmedName) : getColorByName(project.name);
  const createdDate = formatProjectDate(project.createdAt);

  const canSave = nameField.isValid && !loading;

  const helperText = nameField.showError
    ? 'Введите название проекта'
    : 'Название отображается в портфеле и боковом меню';

  /* START_METHOD_handleClose */
  const handleClose = () => {
    if (loading) return;
    resetFormReady();
    onClose();
  };
  /* END_METHOD_handleClose */

  /* START_METHOD_handleUpdate */
  /* START_CONTRACT: handleUpdate
   * PURPOSE: Проверить форму и обновить проект; при успехе закрыть диалог.
   * INPUTS: { event?: React.FormEvent }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: onUpdate (HTTP); setLoading; onClose.
   * LINKS: M-FE-PROJECTS
   * END_CONTRACT: handleUpdate
   */
  const handleUpdate = async (event) => {
    if (event) event.preventDefault();
    if (!canSave) {
      /* START_BLOCK_SUBMIT_BLOCKED */
      nameField.touch();
      return;
      /* END_BLOCK_SUBMIT_BLOCKED */
    }
    setLoading(true);
    try {
      await onUpdate(project.id, { name: trimmedName });
      /* START_BLOCK_SUBMIT_OK */
      onClose();
      /* END_BLOCK_SUBMIT_OK */
    } finally {
      setLoading(false);
    }
  };
  /* END_METHOD_handleUpdate */

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="edit-project-title"
      slotProps={{
        transition: dialogTransitionSlotProps,
        paper: {
          sx: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100% - 64px)',
            overflow: 'hidden',
          },
        },
      }}
    >
      <Box
        component="form"
        onSubmit={handleUpdate}
        noValidate
        autoComplete={APP_FORM_AUTOCOMPLETE}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          flex: 1,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: 3,
            pt: 2.5,
            pb: 2,
            borderBottom: 1,
            borderColor: 'divider',
            backgroundImage: (theme) =>
              `linear-gradient(180deg, ${alpha(accentColor, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 48%, transparent 100%)`,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              display: 'block',
              color: 'primary.main',
              fontWeight: 600,
              letterSpacing: '0.12em',
              lineHeight: 1.4,
              mb: 0.5,
            }}
          >
            Портфель
          </Typography>
          <Typography
            id="edit-project-title"
            variant="h5"
            component="h2"
            noWrap
            sx={{
              fontFamily: displayFont,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: 1.2,
            }}
          >
            {previewName}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {createdDate
              ? `Создан ${createdDate}. Измените название или настройте накладное время команды.`
              : 'Измените название или настройте накладное время команды.'}
          </Typography>
        </Box>

        <DialogContent
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            pt: 2.5,
          }}
        >
          <Paper
            variant="outlined"
            aria-hidden
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              pl: 2.5,
              pr: 2,
              py: 1.5,
              mb: 2.5,
              overflow: 'hidden',
              borderColor: alpha(accentColor, 0.25),
              bgcolor: alpha(accentColor, 0.04),
              transition: 'border-color 0.24s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.24s cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                bgcolor: accentColor,
                transition: 'background-color 0.24s cubic-bezier(0.22, 1, 0.36, 1)',
              }}
            />
            <ProjectAvatar name={trimmedName || project.name} size={36} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                {previewName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Карточка в списке проектов
              </Typography>
            </Box>
          </Paper>

          <TextField
            inputRef={firstFieldRef}
            required
            fullWidth
            label="Название"
            name="teamplanner-project-name-edit"
            value={nameField.value}
            onChange={nameField.onChange}
            onBlur={nameField.onBlur}
            size="small"
            disabled={loading}
            error={nameField.showError}
            helperText={helperText}
            slotProps={mergeAppDataFieldSlotProps({
              htmlInput: { maxLength: 200 },
            })}
          />

          <Paper
            variant="outlined"
            sx={{
              mt: 3,
              p: 2,
              bgcolor: 'background.default',
              borderColor: 'divider',
            }}
          >
            <ProjectOverheadSection projectId={project.id} disabled={loading} active={open} />
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            flexShrink: 0,
            px: 3,
            py: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Button size="small" onClick={handleClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="small"
            loading={loading}
            disabled={!nameField.isValid}
          >
            Сохранить изменения
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
/* END_METHOD_EditProjectDialog */
